import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryProvider } from "@/providers/QueryProvider";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { logger } from "@/lib/logger";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import PublicLayout from "./components/PublicLayout";
import HowItWorksPage from "./pages/public/HowItWorksPage";
import WhyUsPage from "./pages/public/WhyUsPage";
import StoriesPage from "./pages/public/StoriesPage";
import CalculatorPage from "./pages/public/CalculatorPage";
import AboutPage from "./pages/public/AboutPage";
import ContactPage from "./pages/public/ContactPage";
import { PaymentSuccess } from "./pages/PaymentSuccess";
import { PaymentCancel } from "./pages/PaymentCancel";
import CustomerDashboard from "./pages/CustomerDashboard";
import BorrowPage from "./pages/customer/BorrowPage";
import MyLoansPage from "./pages/customer/MyLoansPage";
import KycPage from "./pages/customer/KycPage";
import { NotificationsPage } from "./pages/customer/NotificationsPage";
import AccountPage from "./pages/customer/AccountPage";
import PhoneAuth from "./pages/PhoneAuth";
import StaffLogin from "./pages/staff/StaffLogin";
import StaffShell from "./pages/staff/StaffShell";
import { ApplicationsQueue, KycQueue } from "./pages/staff/WorkPages";
import { ExecutiveDashboard } from "./pages/staff/ExecutiveDashboard";
import { LoansPage as StaffLoansPage, ClientsPage as StaffClientsPage, ReconciliationPage, AuditPage } from "./pages/staff/OpsPages";
import { SalesPage } from "./pages/staff/SalesPage";
import { PaymentsPage } from "./pages/staff/PaymentsPage";
import { DisbursementsPage } from "./pages/staff/DisbursementsPage";
import { CustomerDetailPage } from "./pages/staff/CustomerDetailPage";
import { LoanDetailPage } from "./pages/staff/LoanDetailPage";
import { ProductsPage } from "./pages/staff/ProductsPage";
import { UsersPage } from "./pages/staff/UsersPage";
import { SecurityPage } from "./pages/staff/SecurityPage";
import { CollectionsPage } from "./pages/staff/CollectionsPage";
import { PlatformAuthProvider, PlatformProtectedRoute } from "./platform/PlatformAuthContext";

const App = () => {
  // Error handler for boundary
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    logger.error('React Error Boundary caught error', {
      componentStack: errorInfo.componentStack,
      error: error.message,
      stack: error.stack,
    });
  };

  return (
    <ErrorBoundary onError={handleError}>
      <QueryProvider>
        <TooltipProvider>
          <PlatformAuthProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Routes>
                {/* Public pages — shared nav + footer via PublicLayout */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/how-it-works" element={<HowItWorksPage />} />
                  <Route path="/why-us" element={<WhyUsPage />} />
                  <Route path="/stories" element={<StoriesPage />} />
                  <Route path="/calculator" element={<CalculatorPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/login" element={<PhoneAuth />} />
                  <Route path="/register" element={<PhoneAuth />} />
                </Route>

                {/* Staff portal — platform auth. Old /admin permanently redirects. */}
                <Route path="/staff/login" element={<StaffLogin />} />
                <Route path="/staff" element={<StaffShell />}>
                  <Route index element={<ExecutiveDashboard />} />
                  <Route path="applications" element={<ApplicationsQueue />} />
                  <Route path="kyc" element={<KycQueue />} />
                  <Route path="collections" element={<CollectionsPage />} />
                  <Route path="reconciliation" element={<ReconciliationPage />} />
                  <Route path="loans" element={<StaffLoansPage />} />
                  <Route path="loans/:loanId" element={<LoanDetailPage />} />
                  <Route path="disbursements" element={<DisbursementsPage />} />
                  <Route path="payments" element={<PaymentsPage />} />
                  <Route path="clients" element={<StaffClientsPage />} />
                  <Route path="clients/:customerId" element={<CustomerDetailPage />} />
                  <Route path="audit" element={<AuditPage />} />
                  <Route path="sales" element={<SalesPage />} />
                  <Route path="products" element={<ProductsPage />} />
                  <Route path="regions" element={<UsersPage />} />
                  <Route path="security" element={<SecurityPage />} />
                </Route>
                <Route path="/admin/*" element={<Navigate to="/staff" replace />} />

                {/* Customer Portal — sidebar layout with nested pages */}
                <Route path="/dashboard" element={
                  <PlatformProtectedRoute>
                    <CustomerDashboard />
                  </PlatformProtectedRoute>
                }>
                  <Route index element={<BorrowPage />} />
                  <Route path="loans" element={<MyLoansPage />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="kyc" element={<KycPage />} />
                  <Route path="account" element={<AccountPage />} />
                </Route>

                {/* Payment Callbacks */}
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancel" element={<PaymentCancel />} />

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </PlatformAuthProvider>
        </TooltipProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
};

export default App;
